import {tv, type VariantProps} from 'tailwind-variants'
import {HgAspectRatio} from '~/design-system/hg/components'
import HgArticleHeroText, {
  type HgArticleHeroTextProps,
} from '~/design-system/hg/components/HgArticleHeroText'
import {type AuraColorToken} from '~/design-system/hg/tokens/colors'
import {getHexValueFromToken} from '~/design-system/hg/tokens/colors/utils'
import {cn, unreachableCase} from '~/design-system/utils'

const hgArticleHeroLowVariants = tv({
  slots: {
    outerWrapper: 'px-s5',
    innerWrapper: 'grid place-items-center gap-s4 sm:grid-cols-2 sm:gap-s4',
    asset: '',
    aspectRatioWrapper: '',
  },
  variants: {
    assetType: {
      horizontal: {
        outerWrapper: 'pt-s8',
        asset: 'w-full',
      },
      square: {
        outerWrapper: 'pt-s8',
        aspectRatioWrapper: 'lg:grid lg:grid-cols-8 lg:gap-s4',
        asset: 'sm:px-s8 lg:col-span-6 lg:col-start-2 lg:px-0',
      },
      vertical: {
        outerWrapper: 'pt-s5',
        aspectRatioWrapper: 'lg:grid lg:grid-cols-8 lg:gap-s4',
        asset: 'sm:px-s8 lg:col-span-6 lg:col-start-2 lg:px-0',
      },
    },
    hasBackgroundColor: {
      true: {},
    },
  },
  compoundVariants: [
    {
      assetType: 'horizontal',
      hasBackgroundColor: true,
      className: {
        outerWrapper: 'pb-s8',
      },
    },
    {
      assetType: 'square',
      hasBackgroundColor: true,
      className: {
        outerWrapper: 'pb-s8',
      },
    },
    {
      assetType: 'vertical',
      hasBackgroundColor: true,
      className: {
        outerWrapper: 'pb-s5',
      },
    },
  ],
})

export type HgArticleHeroLowVariantProps = {
  assetType: NonNullable<VariantProps<typeof hgArticleHeroLowVariants>['assetType']>
  backgroundColorToken?: AuraColorToken
  className?: string
  asset: React.ReactNode
} & HgArticleHeroTextProps

export default function HgArticleHeroLowVariantComponents({
  assetType,
  asset,
  backgroundColorToken,
  className,
  ...articleHeroTextProps
}: HgArticleHeroLowVariantProps) {
  const backgroundColor = getHexValueFromToken(backgroundColorToken)

  const {
    asset: assetVariant,
    outerWrapper,
    innerWrapper,
    aspectRatioWrapper,
  } = hgArticleHeroLowVariants({
    assetType,
    hasBackgroundColor: !!backgroundColorToken,
  })

  const getAspectRatio = () => {
    switch (assetType) {
      case 'horizontal':
        return '16:9'
      case 'square':
        return '1:1'
      case 'vertical':
        return '4:5'
      default:
        return unreachableCase(assetType)
    }
  }

  return (
    <div
      style={{'--background': backgroundColor} as React.CSSProperties}
      className={cn(
        !!backgroundColor && 'bg-[var(--background)]',
        'pt-blog-navbars-height'
      )}
    >
      <div className={outerWrapper()}>
        <div className={innerWrapper({className})}>
          <HgArticleHeroText {...articleHeroTextProps} />
          <HgAspectRatio
            classNames={{
              wrapper: aspectRatioWrapper(),
              assetWrapper: assetVariant(),
            }}
            aspectRatio={getAspectRatio()}
            borderRadius="0px"
          >
            {asset}
          </HgAspectRatio>
        </div>
      </div>
    </div>
  )
}
